import { SingleValue } from "react-select";
import { queryClient } from "@/ajax/queryClient";
import { StyledAsyncSelect } from "./StyledAsyncSelect";
import { useTRPC } from "@/trpc";
import { useOrganization } from "@/hooks/use-organization";

interface FacilitiesSelectProps {
  value?: {
    name: string;
    id: string;
    address1: string | null;
    address2?: string | null;
    city: string | null;
    state: string | null;
    zipCode: string | null;
    isRemote: boolean | null;
  };
  onChange?: (
    value: {
      name: string;
      id: string;
      address1: string | null;
      address2?: string | null;
      city: string | null;
      state: string | null;
      zipCode: string | null;
      isRemote: boolean | null;
    } | null,
  ) => void;
  placeholder?: string;
  disabled?: boolean;
}

export const FacilitySelect = ({
  value,
  onChange,
  placeholder = "Select facility",
  disabled = false,
}: FacilitiesSelectProps) => {
  const trpc = useTRPC();
  const { selectedOrg, selectedOrgBusinessUnit } = useOrganization();

  const loadFacilities = async (inputValue: string) => {
    const facilityOptions = await queryClient.fetchQuery(
      trpc.facility.getAll.queryOptions({
        organizationId: selectedOrg?.id || "",
        organizationBusinessUnitId: selectedOrgBusinessUnit.id,
      }),
    );
    const lowerInput = inputValue.toLowerCase();
    return facilityOptions
      .map((facility) => ({
        label: facility.name,
        value: facility.id,
        address1: facility.address1,
        address2: facility.address2,
        city: facility.city,
        state: facility.state,
        zipCode: facility.zipCode,
        isRemote: facility.isRemote,
      }))
      .map((option) => ({
        ...option,
        score: option.label.toLowerCase().indexOf(lowerInput),
      }))
      .filter((option) => option.score >= 0)
      .sort((a, b) => {
        // Exact matches first
        if (a.score === 0 && b.score !== 0) {
          return -1;
        }
        if (b.score === 0 && a.score !== 0) {
          return 1;
        }
        // Then by position of match
        return a.score - b.score;
      });
  };

  const handleChange = (
    option: SingleValue<{ label: string; value: string }> | null,
  ) => {
    // For single-select, option is a single object
    const singleOption = option as SingleValue<{
      label: string;
      value: string;
      address1: string;
      address2: string | undefined;
      city: string;
      state: string;
      zipCode: string;
      isRemote: boolean;
    }>;
    onChange?.(
      singleOption
        ? {
            name: singleOption.label,
            id: singleOption.value,
            address1: singleOption.address1,
            address2: singleOption.address2,
            city: singleOption.city,
            state: singleOption.state,
            zipCode: singleOption.zipCode,
            isRemote: singleOption.isRemote,
          }
        : null,
    );
  };

  let inputValue = undefined;
  if (value) {
    inputValue = {
      label: value.name,
      value: value.id,
    };
  }
  return (
    <StyledAsyncSelect
      value={inputValue}
      loadOptions={loadFacilities}
      onChange={handleChange}
      defaultOptions={true}
      isDisabled={disabled}
      placeholder={placeholder}
      isMulti={false}
      isClearable
      cacheOptions={false}
    />
  );
};
